import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { userFromAccount } from '@shared/auth/user-from-account.helper';
import type { UserRole } from '@domain/models/user.interface';
import { RequireAuth } from './require-auth';

type Props = {
  /** One or more roles; the user must have **at least one**. */
  readonly role: UserRole | readonly UserRole[];
  readonly children: ReactNode;
};

/**
 * Role-based route guard. Wraps `<RequireAuth>` so unauthenticated
 * users still get redirected to sign-in; once authenticated, the
 * active account's `idTokenClaims.roles` is checked. On mismatch
 * the user is redirected to `/403`.
 */
export function RequireRole({ role, children }: Props): JSX.Element {
  return (
    <RequireAuth>
      <RoleGate role={role}>{children}</RoleGate>
    </RequireAuth>
  );
}

function RoleGate({ role, children }: Props): JSX.Element {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  if (!isAuthenticated) {
    return <></>;
  }

  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
  if (!account) {
    return <></>;
  }

  const user = userFromAccount(account);
  const required = normaliseRoles(role);

  const hasRole = user.roles.some((r) => required.includes(r));
  if (!hasRole) {
    return <Navigate to="/403" replace />;
  }

  return <>{children}</>;
}

function normaliseRoles(role: UserRole | readonly UserRole[]): readonly UserRole[] {
  return Array.isArray(role) ? role : [role as UserRole];
}
